/**
 * useQuotaExitBanner.js
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-463 · Phase 5.5a · Quota + Exit-Banner + Grace-Timer Hook
 * Date:     2026-09-02
 * Umbrella: IJ-076 · Refactor Programme 2026 · Phase 5
 * Owner:    E1
 *
 * Purpose:
 *   Lift the quota, exit-banner and grace-timer cluster out of
 *   KnowledgeGraphPage.jsx into a discrete state slice. Every handler
 *   and effect body below is byte-verbatim with the pre-extraction
 *   parent. Zero behaviour change.
 *
 * Owns:
 *   - demoViewed state (a UX-only "has the user viewed the P&L Demo"
 *     signal shared with the parent's demo runner and coach-mark).
 *   - Exit-banner state: exitBannerVariant, graceStartedAt,
 *     gracePctRemaining, GRACE_MS.
 *   - Refs: bannerDismissedRef, sessionStartRef, dismissalTokenRef.
 *   - billedCount + incrementBilledCount.
 *   - handleQuotaExceededError (402 handler).
 *   - Six effects (localStorage cleanup, mount hydrate, persist
 *     demoViewed, auto-fire completed banner, 10-min grace timer,
 *     server-side fire-banner lock) plus handleExitClick.
 *
 * The download coach-mark cluster stays in the parent (demo-scoped, not
 * quota-scoped) and reads demoViewed from this hook's return.
 */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { clearToken } from '../utils/authClient';

export function useQuotaExitBanner({ api, API, isAdmin, onLogout }) {
  // Has the user viewed the demo this session? (Required to expire session along with 3 used queries.)
  const [demoViewed, setDemoViewed] = useState(false);

  // ── Beta investor-campaign exit banners ───────────────────────────────────
  // Non-blocking bottom banners triggered by either:
  //   • completed: 3 queries + 1 Demo = 4 actions done (auto-fires + starts
  //     10-min grace timer; on timeout, auto-redirect to marketing site)
  //   • leave_early: user clicked the Exit button before completing all 4
  // Both gated to non-admins so testing on the startech admin account doesn't
  // trigger them. testharness_nonadmin is the non-admin account for QA.
  const [exitBannerVariant, setExitBannerVariant] = useState(null);
  const [graceStartedAt, setGraceStartedAt] = useState(null);
  const [gracePctRemaining, setGracePctRemaining] = useState(100);
  const GRACE_MS = 10 * 60 * 1000;  // 10 minutes
  // D1 review · 2026-02-09 · operator directive:
  //   "Keep user in the internal site for 10min and then kick out
  //    (or they can exit on their own prior to 10min window ending)."
  // The banner's Got-it button used to no-op because the auto-fire
  // useEffect below immediately re-created the banner as soon as it was
  // dismissed. Track dismissal in a ref so re-fire is suppressed for
  // the remainder of the session. Ref resets on full-page refresh which
  // is intentional — same lifecycle as billedCount.
  const bannerDismissedRef = useRef(false);
  // 2026-02-13 — operator-approved exit-banner analytics tag. Captures
  // the moment the KG page mounted post-login so the [Got it] click can
  // compute `session_duration_ms` for the /exit-banner/dismissed POST.
  // Ref (not state) — a re-render must never reset the anchor.
  const sessionStartRef = useRef(Date.now());
  // IJ-215 (2026-08-01) — captures the short-lived dismissal-token minted by
  // POST /exit-banner/fire so the [Got it] click can authorise POST
  // /exit-banner/dismissed WITHOUT sending the now-revoked JWT.
  // Replaces the prior verify_token_lax workaround (OWASP A01 REMEDIATED).
  const dismissalTokenRef = useRef(null);
  // ───────────────────────────────────────────────────────────────────────────

  // Per-session billed-query counter. Lives ONLY in React state — no
  // localStorage / sessionStorage persistence. This means:
  //   • Survives thread replacement (e.g. running the demo wipes the
  //     visible thread but the counter stays accurate).
  //   • Resets to 0 on full page refresh, browser close, or new tab —
  //     i.e. every fresh load = a fresh trial session, per spec.
  //
  // ⚠️ NOTE (Feb 28, 2026): client counter is now a UX hint only — server
  // enforces the 3-query cap via /api/quota gate on /query/start and
  // /product_search. A 402 response triggers the Exit Banner regardless
  // of this value. Helper below catches 402s from any API call.
  const [billedCount, setBilledCount] = useState(0);
  const incrementBilledCount = () => {
    setBilledCount((c) => Math.min(c + 1, 99));
  };

  // Handle backend 402 "quota_exceeded" responses. Fires the Exit Banner
  // (completion variant) regardless of demo state — server is the source
  // of truth, so if it says "no more queries", the user is done.
  // Returns true if the error was a 402 and was handled; false otherwise.
  const handleQuotaExceededError = (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const isQuotaError =
      status === 402 ||
      (detail && typeof detail === 'object' && detail.code === 'quota_exceeded');
    if (!isQuotaError) return false;
    // Sync UI counter to the server-reported count so the hover/badge UI
    // matches reality on the next render.
    const billed = detail?.billable_count ?? 3;
    setBilledCount(Math.max(billed, 3));
    // D5-7 (2026-02-13) — operator directive: "Prior to this received
    // Exit Screen and clicked Got It. I hadn't yet viewed the Demo."
    // The prior logic hard-coded 'completed' whenever quota was
    // exhausted, but 'completed' semantically means "all 4 tasks done"
    // (3 queries + Demo view). If the user hasn't viewed the Demo yet
    // they still have an action left, so the 'leave_early' variant
    // (whose copy is "you can still take some actions") is the correct
    // one. The auto-fire useEffect above already gates 'completed' on
    // billedCount >= 3 && demoViewed, so this handler now mirrors that.
    if (!exitBannerVariant && !bannerDismissedRef.current) {
      const nextVariant = demoViewed ? 'completed' : 'leave_early';
      setExitBannerVariant(nextVariant);
      if (nextVariant === 'completed' && !graceStartedAt) {
        setGraceStartedAt(Date.now());
      }
    }
    toast.error(detail?.message || 'Search quota reached');
    return true;
  };

  // One-time cleanup of the stale localStorage key from the previous
  // (incorrect) persistence attempt. Safe to run on every mount; a no-op
  // once cleared.
  useEffect(() => {
    try { localStorage.removeItem('si_billed_count'); } catch {}
  }, []);

  // D5-6 (2026-02-13) — hydrate `billedCount` + `demoViewed` on mount so
  // the sticky QuotaDemoBox re-appears after the user navigates away
  // (e.g. Sitemap → back) and comes back to KGPage. billedCount comes
  // from the server (authoritative — `/api/quota/status`). demoViewed
  // is a UX-only signal so it's OK to cache in localStorage; the server
  // never uses it for enforcement. Fail-OPEN on network errors — a
  // stale in-memory 0 is better than a login-storm.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${API}/quota/status`);
        if (cancelled) return;
        const n = Number(res?.data?.billable_count ?? 0);
        if (Number.isFinite(n) && n > 0) setBilledCount(Math.min(3, n));
      } catch { /* fail-open */ }
    })();
    try {
      if (localStorage.getItem('si_demo_viewed') === '1') setDemoViewed(true);
    } catch { /* noop */ }
    return () => { cancelled = true; };
  }, []);

  // Persist demoViewed so the P&L Demo doesn't have to be re-fired
  // after a Sitemap → back navigation. localStorage (not sessionStorage)
  // because "have I ever viewed the demo" is a persistent UX signal for
  // the operator's beta campaign, not a per-session one.
  useEffect(() => {
    try {
      if (demoViewed) localStorage.setItem('si_demo_viewed', '1');
    } catch { /* noop */ }
  }, [demoViewed]);

  // ── Auto-fire the "completed" exit banner when the user has done all
  // 4 actions (3 queries + 1 Demo). Gated to non-admins so admin QA on
  // startech doesn't trigger. The banner is non-blocking — user can keep
  // browsing Control Centre / Accuracy / Menu during the 10-min grace
  // period until the timer auto-redirects them to the marketing site.
  // D1 review · 2026-02-09: also gated on `bannerDismissedRef` so a
  // Got-it click doesn't immediately re-fire this useEffect and re-show
  // the banner on the next render tick.
  useEffect(() => {
    if (isAdmin) return;
    if (exitBannerVariant) return;  // already showing
    if (bannerDismissedRef.current) return;  // user dismissed this session
    if (billedCount >= 3 && demoViewed) {
      setExitBannerVariant('completed');
      if (!graceStartedAt) setGraceStartedAt(Date.now());
    }
  }, [isAdmin, billedCount, demoViewed, exitBannerVariant, graceStartedAt]);

  // ── 10-min grace timer for the completion banner. Auto-redirects to the
  // marketing site on expiry.
  // D1 review · 2026-02-09: timer now depends ONLY on `graceStartedAt` so
  // the countdown keeps running even after the user clicks Got-it. Prior
  // behaviour tied the timer to `exitBannerVariant === 'completed'` which
  // froze the countdown on dismiss — operator explicitly wants the 10-min
  // window to keep running whether the banner is visible or dismissed.
  useEffect(() => {
    if (!graceStartedAt) return;
    const tick = () => {
      const elapsed = Date.now() - graceStartedAt;
      const remaining = Math.max(0, GRACE_MS - elapsed);
      setGracePctRemaining((remaining / GRACE_MS) * 100);
      if (remaining <= 0) {
        // D2-#1 · SPD v5.76 (2026-07-12) — clear token before the
        // marketing-site redirect so the user can't just navigate back
        // to beta.search.inc and land re-authenticated.
        clearToken();
        const url = (process.env.REACT_APP_MARKETING_URL || 'https://www.search.inc').trim();
        window.location.assign(url);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [graceStartedAt]);

  // D2-#1 · SPD v5.76 (2026-07-12) — session-persistence fix.
  // On Exit, non-admin beta users must be forced through /login again on
  // next visit. Previously the JWT stayed in localStorage so re-typing
  // the URL landed them back in without any credentials. The token is
  // wiped inside the fire-banner useEffect below AFTER the /exit-banner/fire
  // POST completes — do NOT wipe it here, otherwise `authAxios`'s per-
  // request interceptor reads null from localStorage and the fire POST
  // goes out unauthenticated (401 from backend), silently skipping the
  // server-side `_revoked_users.add()`. This was caught by testing
  // agent iteration 67 review comment #4.
  const handleExitClick = () => {
    if (isAdmin) {
      onLogout();
      return;
    }
    if (exitBannerVariant === 'completed') return;  // completion banner has priority
    setExitBannerVariant('leave_early');
  };

  // ── Server-side exit-flow lock. When the banner fires (any variant),
  // persist a tenant-keyed row in v3_exit_event so re-login on the same
  // username is blocked for the retention window. Admin + harness are
  // exempt server-side, so a stray call for them is a no-op. Idempotent
  // by design — duplicate writes are benign.
  const exitBannerFiredRef = useRef(false);
  useEffect(() => {
    if (!exitBannerVariant) return;
    if (exitBannerFiredRef.current) return;
    if (isAdmin) return;  // skip the network round-trip for admins
    exitBannerFiredRef.current = true;
    (async () => {
      try {
        const res = await api.post(`${API}/exit-banner/fire`, { variant: exitBannerVariant });
        // IJ-215 · capture the dismissal-token so the [Got it] click can
        // authorise POST /exit-banner/dismissed without a JWT.
        const tok = res && res.data && res.data.dismissal_token;
        if (tok) dismissalTokenRef.current = tok;
      } catch (e) {
        // Non-fatal — exit flow remains client-side intact even if the
        // server log fails. Server is fail-OPEN by design.
        console.warn('exit-banner fire failed:', e?.response?.status);
      } finally {
        // D2-#1 · SPD v5.76 (2026-07-12) — clear the token AFTER the
        // fire POST completes (whether or not it succeeded). This is
        // the second half of the sequenced-revocation fix: token is
        // cleared to force /login on the next URL type-in, and the
        // server-side `_revoked_users` set has already been updated
        // (assuming the fire POST succeeded) to reject any surviving
        // JWT that a copy of the app might still hold on another tab.
        clearToken();
      }
    })();
  }, [exitBannerVariant, isAdmin]);

  return {
    billedCount,
    incrementBilledCount,
    handleQuotaExceededError,
    demoViewed,
    setDemoViewed,
    exitBannerVariant,
    setExitBannerVariant,
    gracePctRemaining,
    handleExitClick,
    bannerDismissedRef,
    sessionStartRef,
    dismissalTokenRef,
  };
}
